import Vue from 'vue';
import { Helper } from '@/services/helper/helper';
import { AdServiceHelper } from '@/services/helper/adServiceHelper';
import Teaser, { CAPTION_COLORS, CAPTION_POSITION, LABEL_COLORS } from '@/components/Teaser/Teaser';

export const AD_BLOCKER_TYPES = {
	none: 0,
	trackingBlocker: 1,
	adBlocker: 2
};

/**
 * Class providing functions for communication with AdService 2.
 * Is used static. Do not instantiate!
 * @abstract
 */
class AdServiceWrapper {
	/**
	 * Initializes all the data.
	 * @static
	 * @param {string} dataName - The value for data attributes [data-[dataName]-slot].
	 * @param {object} additionalParams - Additional Parameters for AdService 2 initialization.
	 * @param {number} colCount - Current column state of homepage. Needed for statistics on AdServer.
	 */
	static init (dataName, additionalParams, colCount) {
		this.serviceResponse = window.initialData.serviceResponse;

		if (dataName && typeof dataName === 'string' && dataName.length > 0) {
			this.setDataName(dataName);
		}

		let connectorVersion = 7;
		const forcedVersion = Helper.getUrlParam('connectorVersion');
		if (forcedVersion) {
			if (forcedVersion === 'prev') {
				connectorVersion--;
				if (connectorVersion <= 0) {
					connectorVersion = 1;
				}
			} else if (forcedVersion === 'next') {
				connectorVersion++;
			} else {
				const forcedVersionAsInt = parseInt(forcedVersion);
				connectorVersion = forcedVersionAsInt || connectorVersion;
			}
		}
		console.log('[AdService Connector] Version: ' + connectorVersion);

		const connectorUrl = 'https://dl.' + window.SiteInfo.brandShort + '.' + window.SiteInfo.tld + '/uim/connector/' + connectorVersion + '/';
		const basicConfig = {
			'nonfriendlyiframe-document': connectorUrl + 'nonfriendlyiframe.html',
			'adservice-iframe-script-url': connectorUrl + 'service-connector.min.js',
			'request-ad-only-if-visible': false,
			'secure': true,
			'request-async': true
		};
		this.setConfig(basicConfig);
		this.initParams(additionalParams, colCount);

		AdServiceHelper.listenEvent('initialize.end', () => {
			this.setNativeTeaserTemplate();
			window.AdService.Module('uim-cookie-exchanger').run();
			this.incrementAc();
		});
	}

	/**
	 * Sets the data-[dataName]-slot for Ad Containers.
	 * @static
	 * @param {string} dataName - The attribute name.
	 */
	static setDataName (dataName) {
		this.setConfig({
			'data-attribute-namespace': dataName
		});
	}

	/**
	 * Sets configuration for AdService 2.
	 * @static
	 * @param {object} confObject - The object with the configuration fields and values.
	 */
	static setConfig (confObject) {
		AdServiceHelper.listenEvent('initialize.end', () => {
			window.AdService.setConfig(confObject);
		});
	}

	/**
	 * Listens to AdService 2 event which executes the 3rd -> 1st party cookie exchange.
	 * Then reads the 1st party cookie and calls the callback function with nguserid as parameter.
	 * @static
	 * @param {callback} func - The callback function to call which gets the nguserid.
	 */
	static getNgUserIdCookie (func) {
		AdServiceHelper.listenEvent('initialize.end', () => {
			if (typeof window.UI_nguserid === 'undefined') {
				AdServiceHelper.listenEvent('module.uim-cookie-exchanger.cookie-set', () => {
					const nguserid = Helper.getCookieValue('NGUserID');
					func(nguserid);
				});
			} else {
				const nguserid = Helper.getCookieValue('NGUserID');
				func(nguserid);
			}
		});
	}

	/**
	 * Sets the CSS for the iframe slots.
	 * @static
	 * @param {string} slot - The slot name to set the CSS for.
	 */
	static setIframeCss (slot) {
		/**
		 * Variable definition to be used for AdService.setIframeCss.
		 * @param {string} action - The function to be called internal in AdService. Should always be 'useIframeCss' in this case.
		 * @param {string} css - The name of the CSS. Used in AdService.setIframeCss as attribute name.
		 */
		const top = {
			rules: [
				{
					action: 'useIframeCss',
					css: 'top'
				}
			]
		};

		const middle = {
			rules: [
				{
					action: 'useIframeCss',
					css: 'middle'
				}
			]
		};

		const left = {
			rules: [
				{
					action: 'useIframeCss',
					css: 'left'
				}
			]
		};

		const right = {
			rules: [
				{
					action: 'useIframeCss',
					css: 'right'
				}
			]
		};

		window.AdService.setIframeCss({
			top: {
				css: 'html, body, body > div { width: 100%; height: 100% } iframe, a { float: right }'
			},
			middle: {
				css: 'html, body, body > div { width: 100%; height: 100% } iframe, body > div > a { display: inline-block; position: relative; left: 50%; transform: translateX(-50%) }'
			},
			left: {
				css: 'html, body, body > div { width: 100%; height: 100% } iframe, a { float: right }'
			},
			right: {
				css: 'html, body, body > div { width: 100%; height: 100% } iframe, a { float: left }'
			}
		});

		window.AdService.on('ad.request', slot, () => {
			let exclusionRules = {};

			if (slot === 'top') {
				exclusionRules.superbanner = top;
			} else if (slot === 'middle') {
				exclusionRules.billboard = middle;
			} else if (slot === 'left') {
				exclusionRules.skyRight = left;
				exclusionRules.sitebar = left;
				exclusionRules.halfpage = left;
			} else if (slot === 'right') {
				exclusionRules.skyRight = right;
				exclusionRules.sitebar = right;
				exclusionRules.halfpage = right;
			}

			window.AdService.setExclusionRuleSet(exclusionRules);
		});
	}

	/**
	 * Initializes parameters needed by AdService2 and AdServer to display correct ads.
	 * @static
	 * @param {object} additionalParams - Can be given if there are additional parameters under certain circumstances.
	 * @param {number} colCount - Current column count.
	 */
	static initParams (additionalParams, colCount) {
		const params = {};
		params.wi = this.serviceResponse._params.wi;
		params.section = this.serviceResponse._params.section;
		params.portal = this.serviceResponse._params.portal;
		params.category = this.serviceResponse._params.category;
		params.layoutclass = this.serviceResponse._params.layoutclass;
		params.nguserid = this.serviceResponse._params['nguserid'];
		params.categorytype = this.serviceResponse.response.type + '_' + colCount;
		params.consentlevel = this.serviceResponse._params['prf[consentLevel]'];
		params.weather_condition = this.serviceResponse._params['prf[weather_condition]'];
		params.weather_temp = this.serviceResponse._params['prf[weather_temp]'];
		params.pagev = 2;
		if (this.serviceResponse._params['prf[busterid]']) {
			params.hpeventid = this.serviceResponse._params['prf[busterid]'];
		}

		if (window.PageConfig.sitespect) {
			params['prf[special]'] = +window.PageConfig.sitespect.special;
		}

		let initParams = Helper.shallowClone(params);

		if (Helper.isObject(additionalParams)) {
			initParams = Helper.mergeObjects(params, additionalParams);
		}
		AdServiceHelper.listenEvent('initialize.end', () => {
			window.AdService.setParam(initParams);
		});
	}

	/**
	 * Calls AdService2 to make an ad for a given slot.
	 * @static
	 * @param {string} slot - The slot name to make ad for.
	 * @param {boolean} [setIframeCss] - If the iFrame CSS should be applied.
	 */
	static makeAd (slot, setIframeCss) {
		if (setIframeCss) {
			this.setIframeCss(slot);
		}

		const slotElement = document.querySelector(`[data-service-slot="${slot}"]`);

		// add channel name to params
		const channelName = Helper.getElementByAttribute(slotElement, '[data-name]')?.previousElementSibling?.querySelector('h2')?.innerText;
		if (channelName) {
			window.AdService.push(() => {
				window.AdService.AdSlot(slot).setParam('hpchannel', channelName.toLowerCase());
			});
		}

		const hasSubtext = typeof Helper.getAttributeValue(slotElement, 'data-has-subtext') === 'string' ? 1 : 0;
		window.AdService.push(() => {
			window.AdService.AdSlot(slot).setParam('subtext', hasSubtext);
		});

		window.AdService.push(slot);
	}

	/**
	 * Logs the current AdService2 version.
	 * @static
	 */
	static logVersion () {
		AdServiceHelper.listenEvent('initialize.end', () => {
			const versionObj = window.AdService.version;
			console.log('[AdService] Version: ' + versionObj.major + '.' + versionObj.minor + '.' + versionObj.revision);
		});
	}

	/**
	 * Creates Native Teaser and Partner Link HTML string for AdService.
	 * @static
	 */
	static setNativeTeaserTemplate () {
		window.AdService.setConfig('render-template-callback', (renderObject) => {
			const renderData = renderObject.render;
			if (renderData._type === 'teaser-hs-image' || renderData._type === 'content-slider-news') {
				let caption = null;
				let label = null;

				if (renderData._type === 'teaser-hs-image') {
					label = {
						color: LABEL_COLORS.greyLight,
						highlight: 'Anzeige',
						text: renderData.partner
					};
				} else if (renderData.partner || renderData.highlight) {
					label = {
						color: LABEL_COLORS.default,
						highlight: renderData.highlight,
						text: renderData.partner
					};
				}

				if (renderData.caption) {
					caption = {
						text: renderData.caption.text,
						color: renderData.caption.color || CAPTION_COLORS.accent,
						position: renderData.caption.position || CAPTION_POSITION.top
					};
				}

				const TeaserClass = Vue.extend(Teaser);
				const teaserInstance = new TeaserClass({
					propsData: {
						link: {
							href: renderData.clickurl,
							target: renderData.target
						},
						figure: {
							img: {
								src: renderData.imageurl,
								alt: renderData.headline?.replace(/(##br##)/g, '\n'),
								width: 0,
								height: 0
							},
							caption
						},
						article: {
							header: {
								label,
								headline: renderData.headline?.replace(/(##br##)/g, '\n'),
								isDivTag: true
							},
							summary: renderData.subline?.replace(/(##br##)/g, '\n'),
							content: renderData.subline?.replace(/(##br##)/g, '\n')
						}
					}
				});

				teaserInstance.$mount();
				const img = teaserInstance.$el.querySelector('img');

				if (img.dataset.src) {
					img.src = img.dataset.src;
				}

				const articleSummary = teaserInstance.$el.querySelector('p');
				if (articleSummary) {
					articleSummary.style.whiteSpace = 'pre-line';
				}

				if (renderData.count) {
					for (let i = 0; i < renderData.count.length; i++) {
						new Image().src = renderData.count[i];
					}
				}

				return teaserInstance.$el.outerHTML;
			} else if (renderData._type === 'partner-link') {
				const a = document.createElement('a');
				a.href = renderData.clickurl;
				a.classList.add('partner-link');
				a.target = '_blank';
				a.rel = 'sponsored';

				const img = document.createElement('img');
				img.src = renderData.imageurl;

				a.appendChild(img);

				if (renderData.count) {
					new Image().src = renderData.count;
				}

				return a.outerHTML;
			}
		});

		// this part is responsible for setting the right image height
		// it only reacts to elements being rendered with AdService.render() which are only native teasers and the partner links at the moment
		// so we just check if the slot contains box_ or slider_ so we know we only have native teasers
		// we now have to make it this way, because we do not have a reference to the original element anymore
		window.AdService.on('render.end', (event) => {
			if (event?.data?.slot?.id.match(/^(box_|slider_)/)) {
				const cropImage = (img) => {
					img.style.height = '';
					let sixteenbynine = (img.clientWidth / 16) * 9; // Bildhöhe anhand der Breite im 16:9 Format berechnen
					const height = Math.ceil(sixteenbynine / 10) * 10; // 16:9 Höhe auf durch 10 teilbaren Wert erhöhen
					if (height > 1) {
						img.style.height = height + 'px';
					}
				};

				let img = event.data.slot.getDomNode().querySelector('img');
				cropImage(img);

				document.addEventListener('colCountChanged', () => {
					cropImage(img);
				});
			}
		});
	}

	/**
	 * Commits HTML to AdService2 which queues it to put it into an iFrame.
	 * @static
	 * @param {string} slot - The Slot to queue the HTML for.
	 * @param {string} htmlSnippet - The HTML snippet as string.
	 * @param {boolean} refresh - If the slot should be refreshed. Should be true if there's already an ad in there.
	 * @param {string} actualAdType - Sometimes it's necessary to insert the actual AdType in here
	 */
	static queueHtml (slot, htmlSnippet, refresh, actualAdType) {
		AdServiceHelper.listenEvent('initialize.end', () => {
			this.setIframeCss(slot);
			window.AdService.AdSlot(slot).queueAdHtml(htmlSnippet, actualAdType);
			window.AdService.AdSlot(slot).setFixedSize(false);

			if (refresh) {
				window.AdService.refresh(slot);
			} else {
				window.AdService.ad(slot);
			}
		});
	}

	/**
	 * Returns a XML Tag uim:ad with information for AdService2.
	 * @static
	 * @param {string} slot - The slot to return the Tag for.
	 * @param {number} fallbackWidth - The fallback width for the Ad.
	 * @param {number} fallbackHeight - The fallback height for the Ad.
	 * @returns {string} The Tag as a string.
	 */
	static getUIMTag (slot, fallbackWidth, fallbackHeight) {
		return window.initialData.serviceResponse.response.uimAd || this.createUimTag(slot, fallbackWidth, fallbackHeight);
	}

	/**
	 * Creates a XML Tag uim:ad with the size attribute filled.
	 * @static
	 * @param {string} slot - The slot to create the Tag for.
	 * @param {number} fallbackWidth - The fallback width that should be inserted.
	 * @param {number} fallbackHeight - The fallback height that should be inserted.
	 * @returns {string} The Tag as a string.
	 */
	static createUimTag (slot, fallbackWidth, fallbackHeight) {
		const response = this.serviceResponse.response;

		let useWidth;
		let useHeight;

		const billboard = {
			width: 970,
			height: 250
		};
		const sky = {
			width: 160,
			height: 600
		};
		const sitebar = {
			width: 220,
			height: 600
		};

		if (this.fallback) {
			useWidth = fallbackWidth;
			useHeight = fallbackHeight;
		} else {
			switch (response.type) {
				case 'cinematic':
				case 'billboard':
					useWidth = billboard.width;
					useHeight = billboard.height;
					break;
				case 'sitebar':
				case 'double-sitebar':
					useWidth = sitebar.width;
					useHeight = sitebar.height;
					break;
				case 'bridge':
					if (slot === 'middle') {
						useWidth = billboard.width;
						useHeight = billboard.height;
						break;
					}
					if (slot === 'left' || slot === 'right') {
						if ((response.skyVariant !== undefined && response.skyVariant.length === 0) || response.skyVariant === 'dynamic') {
							useWidth = sitebar.width;
							useHeight = sitebar.height;
							break;
						} else {
							useWidth = sky.width;
							useHeight = sky.height;
							break;
						}
					}
					break;
				case 'flagship':
					if (slot === 'middle') {
						useWidth = billboard.width;
						useHeight = billboard.height;
						break;
					}
					if (slot === 'right') {
						useWidth = sitebar.width;
						useHeight = sitebar.height;
						break;
					}
					break;
				default:
			}
		}

		return `<uim:ad adsize="${useWidth}x${useHeight}"></uim:ad>`;
	}

	/**
	 * Computes fallback condition given in initial ad call to see if a fallback should be shown.
	 * @static
	 * @returns {boolean} State if fallback should be shown.
	 */
	static showFallback () {
		const fallbackConditions = window.initialData.serviceResponse.response.showFallback;
		if (!fallbackConditions) {
			this.fallback = false;
			return false;
		}

		const agent = window.initialData.serviceResponse.agent;

		const browserFallback = agent?.browserVersion <= fallbackConditions?.browser?.[agent?.browser]; // Test if browser is even in fallback liste and if so, check if the version is smaller or equal to the fallback condition
		const osFallback = fallbackConditions?.os?.[agent?.os] === true; // Test if the os is true for full os fallback
		const osVersionFallback = agent?.osVersion <= fallbackConditions?.os?.[agent?.os]; // Test if the os version is lower or equal to the fallback condition
		const cleanFallback = !!(!window.initialData.availableResponse && fallbackConditions?.clean);

		const showFallback = !!(browserFallback || osFallback || osVersionFallback || cleanFallback); /* || true */ // If you want to always have a fallback uncomment the true
		this.fallback = showFallback;
		return showFallback;
	}

	/**
	 * Calls AdService2 function to check if AdBlocker is detected.
	 * @static
	 * @param {callback} callBack - Called after AdBlocker Detection is done with the information parameter if AdBlocker was detected.
	 * @fires Document#adbDone
	 * @fires Document#bdDone
	 */
	static checkAdBlocker (callBack) {
		AdServiceHelper.listenEvent('initialize.end', () => {
			window.AdService.setAdserverFallback(() => {
				window.AdService.setDefaultAdServer('adition-asp-united-infos');
			});

			const isBlocked = window.AdService.info.abd().isBlocked;
			document.querySelector('html').setAttribute('data-is-blocked', !!isBlocked);

			const abdDone = new CustomEvent('abdDone', {
				bubbles: true,
				cancelable: true,
				detail: { abd: !!isBlocked, source: 'adServiceWrapper.js' }
			});
			document.querySelector('body').dispatchEvent(abdDone);

			if (isBlocked) {
				const bdDone = new CustomEvent('bdDone', {
					bubbles: true,
					cancelable: true,
					detail: { abd: true, adBlockerType: AD_BLOCKER_TYPES.adBlocker, source: 'adServiceWrapper.js' }
				});
				document.querySelector('body').dispatchEvent(bdDone);
			}

			callBack(isBlocked);
		});
	}

	/**
	 * Creates a script-Tag with src attribute pointing towards an 'available' ad slot.
	 * If the loading of this src fails there is a tracking blocker active.
	 * @static
	 * @param {callback} callBack - Called after Tracking Blocker Detection is done with the information parameter if Tracking Blocker was detected.
	 * @fires Document#tbdDone
	 * @fires Document#bdDone
	 */
	static checkTrackingBlocker (callBack) {
		const tbdDoneOptions = {
			bubbles: true,
			cancelable: true,
			detail: { abd: false, source: 'adServiceWrapper.js' }
		};
		const url = window.AdService.getUrlForSlot('available');

		const scriptFailed = () => {
			window.AdService.setDefaultAdServer('adition-asp-united-uimserv');
			window.AdService.setParam({ deviceclient: 'browser_clean' });

			window.initialData.availableResponse = false;
			tbdDoneOptions.detail.adBlockerType = AD_BLOCKER_TYPES.trackingBlocker;
			const tbdDone = new CustomEvent('tbdDone', tbdDoneOptions);
			document.querySelector('body').dispatchEvent(tbdDone);

			const bdDone = new CustomEvent('bdDone', tbdDoneOptions);
			document.querySelector('body').dispatchEvent(bdDone);
			callBack(true);
		};

		const scriptTag = document.createElement('script');
		scriptTag.onerror = () => {
			scriptFailed();
		};
		scriptTag.onload = () => {
			window.initialData.availableResponse = true;

			tbdDoneOptions.detail.adBlockerType = AD_BLOCKER_TYPES.none;
			const tbdDone = new CustomEvent('tbdDone', tbdDoneOptions);
			document.querySelector('body').dispatchEvent(tbdDone);

			const bdDone = new CustomEvent('bdDone', tbdDoneOptions);
			document.querySelector('body').dispatchEvent(bdDone);

			callBack(false);
		};

		scriptTag.src = url;

		const appendToMe = document.querySelector('[data-available]');
		if (appendToMe) {
			appendToMe.appendChild(scriptTag);
		}

		if (!url || !appendToMe) {
			scriptFailed();
		}
	}

	/**
	 * Loop to create the three partner links in Navigation Bar.
	 * @static
	 * @see {@link AdServiceWrapper.makeAd}
	 */
	static makePartnerlinks () {
		for (let i = 1; i <= 3; i++) {
			document.querySelector(`[data-service-slot=promo_${i}]`) && this.makeAd('promo_' + i);
		}
	}

	/**
	 * Loop to create the ads of Vorteilswelt.
	 * @static
	 * @see {@link AdServiceWrapper.makeAd}
	 */
	static makeVorteilswelt () {
		if (window.PageConfig.vorteilswelt) {
			for (let i = 5; i >= 1; i--) {
				document.querySelector(`[data-service-slot=slider_1_${i}]`) && this.makeAd('slider_1_' + i);
			}
		}
	}

	/**
	 * Increments the Ad Count in AdService2.
	 * Initial AdCall is not counted correctly because it's in the backend so this is necessary before first AdCall in Frontend.
	 * @static
	 */
	static incrementAc () {
		AdServiceHelper.listenEvent('initialize.end', () => {
			window.AdService.getDefaultAdServer().increaseRequestCount();
		});
	}
}

export { AdServiceWrapper };
